/* Flooring Surgeons – collection page (Figma PLP) behaviour. Small, dependency-free, event delegation only, so it
   keeps working after facets.js swaps #ProductGridContainer / the filter groups.
   - Filter drawer on phones (<990px): the desktop filter form slides in from the left (old site); "View N Products"
     shows the live count. Focus stays inside while open (Tab wraps), Esc / backdrop / X close it.
   - Filter groups: one open at a time (old site's accordion; <details name> does it natively, this covers old browsers).
   - Sort: bottom sheet on phones, the old site's dropdown on desktop; both set #SortBy and let facets.js re-render.
   - Price slider: two range inputs synced with Dawn's price fields (facets.js submits on their "input").
   - Price Range in £ per m² (custom.price_m2, a list filter): the From / Up to figures tick the hidden value checkboxes
     inside the range before facets.js reads the form, plus an "r20-30" marker value that puts the figures back.
   - Grid / list view toggle (remembered per browser).
   - "Calculate My Flooring" drawer: saves a room and shows "Total for N m²" on every floor card. */
(() => {
  if (window.FSPlp) return;
  const doc = document;
  const root = doc.documentElement;
  const store = {
    get(k) { try { return JSON.parse(localStorage.getItem(k)); } catch (e) { return null; } },
    set(k, v) { try { v == null ? localStorage.removeItem(k) : localStorage.setItem(k, JSON.stringify(v)); } catch (e) {} },
  };
  // The customer's room is shared with the product-page calculator (fs-calculator.js, key fs-calc-area) and the basket,
  // for this visit only (sessionStorage) – owner, 27 Sep 2026: "should it not bring in the room size I picked in PLP?"
  const visit = {
    get(k) { try { return JSON.parse(sessionStorage.getItem(k)); } catch (e) { return null; } },
    set(k, v) { try { v == null ? sessionStorage.removeItem(k) : sessionStorage.setItem(k, JSON.stringify(v)); } catch (e) {} },
  };
  try { localStorage.removeItem('fs-room'); } catch (e) { /* old long-lived copy */ }
  const money = (pence) => '£' + (pence / 100).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  let lastFocus = null;

  /* ---------- sheets (filters / sort / calculator) ---------- */
  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const filters = () => doc.getElementById('main-collection-filters') || doc.getElementById('main-search-filters');
  const isFilters = (el) => el && el.classList.contains('facets-wrapper');
  const triggers = (el) => el ? doc.querySelectorAll(`[aria-controls="${el.id}"]`) : [];
  let openEl = null;
  const openSheet = (el, focusSel) => {
    if (!el) return;
    lastFocus = doc.activeElement;
    // sheets inside the (animated, transformed) grid container would be positioned against it – lift them to <body>
    if (el.classList.contains('fs-sheet') && el.parentElement !== doc.body) {
      doc.querySelectorAll(`body > .fs-sheet.${el.classList[1]}`).forEach((old) => old.remove());
      doc.body.appendChild(el);
    }
    clearTimeout(el._fsHide);
    el.hidden = false;
    if (isFilters(el)) {
      el.setAttribute('role', 'dialog');
      el.setAttribute('aria-modal', 'true');
    }
    void el.offsetWidth; // start the slide from the closed position
    el.classList.add('is-open');
    openEl = el;
    root.classList.add('fs-lock');
    triggers(el).forEach((b) => b.setAttribute('aria-expanded', 'true'));
    requestAnimationFrame(() => (el.querySelector(focusSel || 'button, input, select, a') || el).focus({ preventScroll: true }));
  };
  const closeSheet = (el) => {
    if (!el || !el.classList.contains('is-open')) return;
    el.classList.remove('is-open');
    if (isFilters(el)) {
      el.removeAttribute('role');
      el.removeAttribute('aria-modal');
    } else {
      el._fsHide = setTimeout(() => (el.hidden = true), reduced() ? 0 : 300); // after the slide-out
    }
    if (openEl === el) openEl = null;
    if (!doc.querySelector('.fs-sheet.is-open, .facets-wrapper.is-open')) root.classList.remove('fs-lock');
    triggers(el).forEach((b) => b.setAttribute('aria-expanded', 'false'));
    lastFocus?.focus?.({ preventScroll: true });
  };

  doc.addEventListener('click', (e) => {
    const t = e.target;
    if (t.closest('[data-fs-sheet-open]')) { openSheet(filters(), '.fs-filters__close'); return; }
    if (t.closest('[data-fs-sheet-close]')) { closeSheet(filters()); return; }
    if (t.closest('[data-fs-sort-open]')) { openSheet(doc.getElementById('FsSortSheet'), '.is-on'); return; }
    if (t.closest('[data-fs-sort-close]')) { closeSheet(doc.getElementById('FsSortSheet')); return; }
    if (t.closest('[data-fs-calc-open]')) { openCalc(); return; }
    if (t.closest('[data-fs-calc-close]')) { closeSheet(doc.getElementById('FsCalc')); return; }
    if (t.closest('[data-fs-calc-clear]')) { visit.set('fs-room', null); visit.set('fs-calc-area', null); fillCalc(); paintTotals(); return; }
    if (t.closest('.fs-sort-dd__btn')) { const dd = t.closest('[data-fs-sort-dd]'); ddOpen(dd) ? ddClose(dd) : ddShow(dd); return; }

    const sortBtn = t.closest('[data-fs-sort]');
    if (sortBtn) {
      const dd = sortBtn.closest('[data-fs-sort-dd]');
      if (dd) ddClose(dd, true);
      else closeSheet(doc.getElementById('FsSortSheet'));
      setSort(sortBtn.dataset.fsSort);
      return;
    }
    doc.querySelectorAll('[data-fs-sort-dd]').forEach((dd) => ddOpen(dd) && ddClose(dd));
    const viewBtn = t.closest('[data-fs-view]');
    if (viewBtn) { store.set('fs-plp-view', viewBtn.dataset.fsView); paintView(); return; }
  });
  doc.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      const dd = [...doc.querySelectorAll('[data-fs-sort-dd]')].find(ddOpen);
      if (dd) { ddClose(dd, true); return; }
      const open = doc.querySelector('.fs-sheet.is-open') || doc.querySelector('.facets-wrapper.is-open');
      if (open) closeSheet(open);
      return;
    }
    // keep Tab inside the open drawer / sheet
    if (e.key === 'Tab' && openEl) {
      const items = [...openEl.querySelectorAll('a[href], button, input, select, summary, [tabindex]:not([tabindex="-1"])')]
        .filter((n) => !n.disabled && n.tabIndex !== -1 && n.offsetParent !== null);
      if (!items.length) return;
      const first = items[0], last = items[items.length - 1];
      if (e.shiftKey && (doc.activeElement === first || !openEl.contains(doc.activeElement))) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && (doc.activeElement === last || !openEl.contains(doc.activeElement))) { e.preventDefault(); first.focus(); }
    }
  });
  // the drawer is desktop's sidebar again from 990px – don't leave the page locked if the window grows
  window.matchMedia('(min-width: 990px)').addEventListener?.('change', (m) => { if (m.matches) closeSheet(filters()); });
  // "Remove Filters" in the drawer footer does nothing when there is nothing to remove
  doc.addEventListener('click', (e) => {
    const a = e.target.closest('.fs-filters__remove.is-disabled');
    if (a) { e.preventDefault(); e.stopImmediatePropagation(); }
  }, true);

  /* ---------- one filter group open at a time ---------- */
  doc.addEventListener('toggle', (e) => {
    const d = e.target;
    if (!(d instanceof HTMLDetailsElement) || !d.open || !d.classList.contains('fs-group')) return;
    d.closest('.fs-filters__groups')?.querySelectorAll('details.fs-group[open]').forEach((o) => { if (o !== d) o.open = false; });
  }, true);

  /* ---------- sort ---------- */
  let refocus = null; // the toolbar is re-rendered with the results – put focus back on the new sort control
  const setSort = (value) => {
    const select = doc.getElementById('SortBy');
    if (select && select.value !== value) {
      refocus = innerWidth >= 990 ? '#FsSortBtn' : '[data-fs-sort-open]';
      select.value = value;
      select.dispatchEvent(new Event('input', { bubbles: true }));
    }
  };
  const ddOpen = (dd) => dd && !dd.querySelector('.fs-sort-dd__list').hidden;
  const ddOpts = (dd) => [...dd.querySelectorAll('.fs-sort-dd__opt')];
  const ddActive = (dd, opt) => {
    ddOpts(dd).forEach((o) => o.classList.toggle('is-active', o === opt));
    const list = dd.querySelector('.fs-sort-dd__list');
    if (opt) { list.setAttribute('aria-activedescendant', opt.id); opt.scrollIntoView({ block: 'nearest' }); }
  };
  const ddShow = (dd) => {
    const list = dd.querySelector('.fs-sort-dd__list');
    list.hidden = false;
    dd.querySelector('.fs-sort-dd__btn').setAttribute('aria-expanded', 'true');
    ddActive(dd, dd.querySelector('.fs-sort-dd__opt[aria-selected="true"]') || ddOpts(dd)[0]);
    list.focus({ preventScroll: true });
  };
  const ddClose = (dd, refocus) => {
    dd.querySelector('.fs-sort-dd__list').hidden = true;
    const btn = dd.querySelector('.fs-sort-dd__btn');
    btn.setAttribute('aria-expanded', 'false');
    if (refocus) btn.focus({ preventScroll: true });
  };
  doc.addEventListener('keydown', (e) => {
    const dd = e.target.closest?.('[data-fs-sort-dd]');
    if (!dd) return;
    const opts = ddOpts(dd);
    if (e.target.classList.contains('fs-sort-dd__btn')) {
      if (['ArrowDown', 'ArrowUp'].includes(e.key)) { e.preventDefault(); ddShow(dd); }
      return;
    }
    if (!ddOpen(dd)) return;
    const cur = opts.findIndex((o) => o.classList.contains('is-active'));
    const go = (i) => { e.preventDefault(); ddActive(dd, opts[Math.max(0, Math.min(opts.length - 1, i))]); };
    if (e.key === 'ArrowDown') go(cur + 1);
    else if (e.key === 'ArrowUp') go(cur - 1);
    else if (e.key === 'Home') go(0);
    else if (e.key === 'End') go(opts.length - 1);
    else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); const o = opts[cur]; ddClose(dd, true); if (o) setSort(o.dataset.fsSort); }
    else if (e.key === 'Tab') ddClose(dd);
  });

  /* ---------- price slider ---------- */
  const syncSlider = (slider) => {
    const [lo, hi] = slider.querySelectorAll('.fs-price__range');
    const max = parseFloat(slider.dataset.max) || 1;
    const a = Math.min(+lo.value, +hi.value), b = Math.max(+lo.value, +hi.value);
    const fill = slider.querySelector('.fs-price__fill');
    if (fill) { fill.style.left = (a / max) * 100 + '%'; fill.style.right = 100 - (b / max) * 100 + '%'; }
  };
  doc.addEventListener('input', (e) => {
    const r = e.target.closest('.fs-price__range');
    if (!r) return;
    e.stopPropagation(); // not a form field of its own – the text inputs carry the value
    const slider = r.closest('[data-fs-price-slider]');
    const [lo, hi] = slider.querySelectorAll('.fs-price__range');
    if (+lo.value > +hi.value) (r === lo ? lo : hi).value = r === lo ? hi.value : lo.value;
    syncSlider(slider);
    const inputs = slider.closest('.fs-group__body').querySelectorAll('.fs-price__input');
    const max = parseFloat(slider.dataset.max);
    inputs[0].value = +lo.value > 0 ? lo.value : '';
    inputs[1].value = +hi.value < max ? hi.value : '';
  }, true);
  doc.addEventListener('change', (e) => {
    const r = e.target.closest('.fs-price__range');
    if (!r) return;
    const input = r.closest('.fs-group__body').querySelectorAll('.fs-price__input')[r.dataset.end === 'min' ? 0 : 1];
    input.dispatchEvent(new Event('input', { bubbles: true }));
  });

  /* ---------- £/m² price range (snippets/fs-plp-filter-group, m2) ----------
     Search & Discovery only offers the £/m² metafield as a list of values, so the figures typed (or slid) tick the hidden
     value checkboxes inside the range (several values = OR). The figures themselves travel in the URL as one more value,
     "r20-30", which matches no product (so it changes nothing, and alone – a range with no floors in it – it gives the
     empty state); it is how a refresh, a shared link or a re-render puts the exact figures back. */
  const M2 = 'filter.p.m.custom.price_m2';
  const m2Num = (v) => { const n = parseFloat(String(v).replace(/[^\d.]/g, '')); return isNaN(n) ? null : n; };
  const m2Apply = (body) => {
    body.dataset.fsM2Done = '1'; // the shopper's figures now – a later refresh must not put the URL's back
    const [a, b] = body.querySelectorAll('.fs-price__input');
    const max = parseFloat(body.dataset.max) || 0;
    let lo = m2Num(a.value), hi = m2Num(b.value);
    if (lo != null && hi != null && lo > hi) [lo, hi] = [hi, lo];
    const ranged = (lo != null && lo > 0) || (hi != null && hi < max);
    body.querySelectorAll('input[data-v]').forEach((cb) => {
      const v = parseFloat(cb.dataset.v);
      cb.checked = ranged && v >= (lo ?? 0) && v <= (hi ?? Infinity);
    });
    const mark = body.querySelector('[data-fs-m2-none]');
    mark.checked = ranged;
    mark.value = ranged ? `r${lo ?? ''}-${hi ?? ''}` : '0';
  };
  doc.addEventListener('input', (e) => {
    const body = e.target.closest?.('[data-fs-m2]');
    if (body && e.target.classList.contains('fs-price__input')) m2Apply(body); // capture: before facets.js reads the form
  }, true);
  const m2Restore = () => {
    const mark = new URLSearchParams(location.search).getAll(M2).find((v) => /^r[\d.]*-[\d.]*$/.test(v));
    if (!mark) return;
    const [lo, hi] = mark.slice(1).split('-');
    doc.querySelectorAll('[data-fs-m2]:not([data-fs-m2-done])').forEach((body) => {
      body.dataset.fsM2Done = '1'; // once per rendered panel
      const box = body.querySelector('[data-fs-m2-none]');
      box.value = mark;
      box.checked = true;
      const [a, b] = body.querySelectorAll('.fs-price__input');
      if (doc.activeElement === a || doc.activeElement === b) return;
      if (a.value !== lo) a.value = lo;
      if (b.value !== hi) b.value = hi;
      const slider = body.querySelector('[data-fs-price-slider]');
      if (!slider) return;
      const max = parseFloat(slider.dataset.max) || 0;
      const [rl, rh] = slider.querySelectorAll('.fs-price__range');
      rl.value = Math.floor(m2Num(lo) ?? 0);
      rh.value = Math.ceil(m2Num(hi) ?? max);
    });
  };

  /* ---------- view toggle ---------- */
  const paintView = () => {
    const view = store.get('fs-plp-view') === 'big' ? 'big' : 'grid'; /* 'big' = 2 per row (was a list view until 5 Oct 2026) */
    /* collections wrap the grid in <collection-component>; search has none – #ProductGridContainer (kept across updates) */
    (doc.querySelector('collection-component') || doc.getElementById('ProductGridContainer'))?.classList.toggle('fs-view-big', view === 'big');
    doc.querySelectorAll('[data-fs-view]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.fsView === view)));
  };

  /* ---------- calculator + card totals ---------- */
  const calc = () => doc.getElementById('FsCalc');
  const room = () => {
    const r = visit.get('fs-room');
    if (r && r.m2) return r;
    const a = visit.get('fs-calc-area'); // typed on a product page this visit
    const m2 = a && parseFloat(a.area);
    return m2 > 0 ? { m2, name: '', mode: 'area', unit: 'm', waste: a.waste === true, raw: { area: String(m2) } } : null;
  };
  // same rule as the product page (FsM2Calculator.packsFor in fs-calculator.js); a room saved before the wastage
  // switch existed has no `waste` field and keeps the old behaviour (10% added)
  const wasteOf = (r) => (r.waste === false ? 0 : 10);
  const packsFor = (m2, waste, pm2) => Math.max(1, Math.ceil(Math.round(((m2 * (1 + waste / 100)) / pm2) * 1000) / 1000));
  const paintTotals = () => {
    const r = room();
    doc.querySelectorAll('.fs-card[data-pack-m2]').forEach((card) => {
      const out = card.querySelector('[data-fs-card-total]');
      if (!out) return;
      const pm2 = parseFloat(card.dataset.packM2), price = parseInt(card.dataset.packPrice, 10);
      if (!r || !r.m2 || !pm2 || !price) { out.hidden = true; return; }
      const waste = wasteOf(r);
      const packs = packsFor(r.m2, waste, pm2);
      out.innerHTML = `<span><b>Total for</b> ${r.m2.toLocaleString('en-GB', { maximumFractionDigits: 2 })} m²${waste ? ' + 10%' : ''}${r.name ? ` · ${r.name.replace(/[<>&"]/g, '')}` : ''}</span><span>${money(packs * price)} <small>(${packs} pack${packs === 1 ? '' : 's'})</small></span>`;
      out.hidden = false;
    });
  };
  const setMode = (form) => {
    const mode = form.elements.mode.value, unit = form.elements.unit.value;
    form.querySelectorAll('[data-show]').forEach((el) => (el.hidden = el.dataset.show !== mode));
    form.querySelectorAll('[data-u-area]').forEach((el) => (el.textContent = unit === 'ft' ? '(ft²)' : '(m²)'));
    form.querySelectorAll('[data-u-len]').forEach((el) => (el.textContent = unit === 'ft' ? '(ft)' : '(m)'));
  };
  const fillCalc = () => {
    const form = calc()?.querySelector('[data-fs-calc-form]');
    if (!form) return;
    const r = room();
    form.querySelector('[data-fs-calc-clear]').hidden = !r;
    form.querySelector('.fs-pcalc__msg').textContent = r ? `Saved: ${r.name ? r.name + ', ' : ''}${r.m2} m²${wasteOf(r) ? ' + 10% wastage' : ''}. Prices show on every floor.` : '';
    if (r) {
      form.elements.mode.value = r.mode || 'area';
      form.elements.unit.value = r.unit || 'm';
      // by name, not form.elements[k]: form.elements.length is the number of controls, not the Length field
      ['area', 'width', 'length', 'room'].forEach((k) => { if (r.raw && r.raw[k] != null) form.querySelector(`[name="${k}"]`).value = r.raw[k]; });
      form.elements.waste.checked = wasteOf(r) > 0;
    }
    setMode(form);
  };
  function openCalc() { fillCalc(); openSheet(calc(), 'input[name="area"]'); }
  doc.addEventListener('change', (e) => {
    const form = e.target.closest('[data-fs-calc-form]');
    if (form && (e.target.name === 'mode' || e.target.name === 'unit')) setMode(form);
  });
  doc.addEventListener('submit', (e) => {
    const form = e.target.closest('[data-fs-calc-form]');
    if (!form) return;
    e.preventDefault();
    const f = form.elements, ft = f.unit.value === 'ft';
    const len = form.querySelector('[name="length"]'); // f.length would be the control count
    let m2;
    if (f.mode.value === 'wl') {
      const w = parseFloat(f.width.value), l = parseFloat(len.value);
      m2 = w > 0 && l > 0 ? w * l * (ft ? 0.09290304 : 1) : 0;
    } else {
      const a = parseFloat(f.area.value);
      m2 = a > 0 ? a * (ft ? 0.09290304 : 1) : 0;
    }
    const msg = form.querySelector('.fs-pcalc__msg');
    if (!m2) { msg.textContent = f.mode.value === 'wl' ? 'Enter the width and length of the room.' : 'Enter the floor area of the room.'; return; }
    m2 = Math.round(m2 * 100) / 100;
    const waste = f.waste.checked;
    visit.set('fs-room', {
      m2, name: f.room.value.trim().slice(0, 40), mode: f.mode.value, unit: f.unit.value, waste,
      raw: { area: f.area.value, width: f.width.value, length: len.value, room: f.room.value },
    });
    visit.set('fs-calc-area', { mode: 'area', area: m2, waste }); // the product page's calculator starts from it
    paintTotals();
    closeSheet(calc());
  });

  /* ---------- after every facets.js / load-more update ---------- */
  const refresh = () => {
    if (refocus && (!doc.activeElement || doc.activeElement === doc.body)) {
      doc.querySelector(`#ProductGridContainer ${refocus}`)?.focus({ preventScroll: true });
      refocus = null;
    }
    paintView();
    paintTotals();
    m2Restore();
    doc.querySelectorAll('[data-fs-price-slider]').forEach(syncSlider);
    const tools = doc.querySelector('.fs-plp-tools[data-count]');
    const n = tools?.dataset.count;
    if (n != null) doc.querySelectorAll('[data-fs-view-count]').forEach((el) => (el.textContent = n));
    const any = !!doc.querySelector('#FacetFiltersForm input[type="checkbox"]:checked') ||
      [...doc.querySelectorAll('#FacetFiltersForm .fs-price__input')].some((i) => i.value);
    doc.querySelectorAll('.fs-filters__remove').forEach((a) => {
      a.classList.toggle('is-disabled', !any);
      any ? a.removeAttribute('aria-disabled') : a.setAttribute('aria-disabled', 'true');
    });
  };
  let queued = false;
  const queue = () => { if (!queued) { queued = true; requestAnimationFrame(() => { queued = false; refresh(); }); } };
  const watch = () => {
    const targets = [doc.getElementById('ProductGridContainer'), doc.getElementById('FacetFiltersForm')].filter(Boolean);
    const mo = new MutationObserver(queue);
    targets.forEach((t) => mo.observe(t, { childList: true, subtree: true }));
  };
  // A link ending in #calculate-flooring (e.g. the measuring guide's "calculator") opens the drawer on arrival
  const deepLink = () => {
    if (location.hash !== '#calculate-flooring' || !calc()) return;
    history.replaceState(null, '', location.pathname + location.search);
    openCalc();
  };
  const init = () => { refresh(); watch(); deepLink(); };
  doc.readyState === 'loading' ? doc.addEventListener('DOMContentLoaded', init) : init();
  window.FSPlp = { refresh };
})();
